import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import Layout from '../components/Layout';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import Badge from '../components/common/Badge';
import Button from '../components/common/Button';

const Dashboard = () => {
  const [loading, setLoading] = useState(true);
  const [agentsStatus, setAgentsStatus] = useState([]);
  const [convStats, setConvStats] = useState({ total: 0, pure_ai: 0, agent: 0, active_now: 0 });
  const [helpdeskSummary, setHelpdeskSummary] = useState({ open_tickets: 0, sla_violations: 0, my_tickets: 0, recent_tickets: [] });
  const [todayBookings, setTodayBookings] = useState({ count: 0, bookings: [] });
  const [assignments, setAssignments] = useState({ active_assignments: 0, in_queue: 0 });
  const [recentActivity, setRecentActivity] = useState([]);
  const [systemHealth, setSystemHealth] = useState({ backend: false, whatsapp: false, database: false, ai: false });
  const [deptPivot, setDeptPivot] = useState([]);
  const [chartDays, setChartDays] = useState(7);
  const chartDaysRef = useRef(7);
  const chartDeptRef = useRef('');
  const [chartDept, setChartDept] = useState('');
  const [departments, setDepartments] = useState([]);
  const [ticketsOverTime, setTicketsOverTime] = useState([]);
  const [ticketsByStatus, setTicketsByStatus] = useState([]);
  const [ticketsByPriority, setTicketsByPriority] = useState([]);
  const [ticketsByDept, setTicketsByDept] = useState([]);
  const [avgResolution, setAvgResolution] = useState([]);
  const [topAgents, setTopAgents] = useState([]);
  const [convsOverTime, setConvsOverTime] = useState([]);
  const [convsByStatus, setConvsByStatus] = useState([]);
  const [agentWorkload, setAgentWorkload] = useState([]);
  const [botVsHuman, setBotVsHuman] = useState({ bot_only: 0, agent_handled: 0, total: 0 });
  const navigate = useNavigate();
  const { isDark } = useTheme();

  useEffect(() => {
    fetchAll();
    const interval = setInterval(fetchAll, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchAll = () => {
    fetchAgentsStatus();
    fetchConvStats();
    fetchHelpdeskSummary();
    fetchTodayBookings();
    fetchAssignments();
    fetchRecentActivity();
    fetchDeptPivot();
    checkSystemHealth();
    setLoading(false);
  };

  const getHeaders = () => {
    const token = localStorage.getItem('token');
    return { 'Authorization': `Bearer ${token}` };
  };

  const fetchAgentsStatus = async () => {
    try {
      const res = await fetch('/api/v1/dashboard/agents-status', { headers: getHeaders() });
      if (res.ok) { const data = await res.json(); if (data.success) setAgentsStatus(data.data || []); }
    } catch (e) { console.error('agents-status error:', e); }
  };

  const fetchConvStats = async () => {
    try {
      const res = await fetch('/api/v1/dashboard/conversation-stats', { headers: getHeaders() });
      if (res.ok) { const data = await res.json(); if (data.success) setConvStats(data.data); }
    } catch (e) { console.error('conv-stats error:', e); }
  };

  const fetchHelpdeskSummary = async () => {
    try {
      const res = await fetch('/api/v1/dashboard/helpdesk-summary', { headers: getHeaders() });
      if (res.ok) { const data = await res.json(); if (data.success) setHelpdeskSummary(data.data); }
    } catch (e) { console.error('helpdesk-summary error:', e); }
  };

  const fetchTodayBookings = async () => {
    try {
      const res = await fetch('/api/v1/dashboard/today-bookings', { headers: getHeaders() });
      if (res.ok) { const data = await res.json(); if (data.success) setTodayBookings(data.data); }
    } catch (e) { console.error('today-bookings error:', e); }
  };

  const fetchAssignments = async () => {
    try {
      const res = await fetch('/api/v1/dashboard/active-assignments', { headers: getHeaders() });
      if (res.ok) { const data = await res.json(); if (data.success) setAssignments(data.data); }
    } catch (e) { console.error('assignments error:', e); }
  };

  const fetchRecentActivity = async () => {
    try {
      const res = await fetch('/api/v1/conversations/recent?limit=5', { headers: getHeaders() });
      if (res.ok) { const data = await res.json(); if (data.success) setRecentActivity(data.data || []); }
    } catch (e) { console.error('recent-activity error:', e); }
  };

  const fetchDeptPivot = async () => {
    try {
      const res = await fetch('/api/v1/dashboard/dept-tickets-pivot', { headers: getHeaders() });
      if (res.ok) { const data = await res.json(); if (data.success) setDeptPivot(data.data || []); }
    } catch (e) { console.error('dept-pivot error:', e); }
  };

  const checkSystemHealth = async () => {
    const h = { backend: false, whatsapp: false, database: false, ai: false };
    try { const r = await fetch('/api/v1/health', { headers: getHeaders() }); if (r.ok) { const d = await r.json(); h.backend = true; h.database = d.database !== false; h.whatsapp = d.whatsapp !== false; h.ai = d.ai !== false; } } catch (e) {}
    setSystemHealth(h);
  };

  const fetchDepartments = async () => {
    try {
      const res = await fetch('/api/v1/helpdesk-agent/departments', { headers: getHeaders() });
      if (res.ok) { const data = await res.json(); if (data.success) setDepartments(data.departments || data.data || []); }
    } catch (e) { console.error('departments error:', e); }
  };

  const fetchChartData = async () => {
    const h = getHeaders();
    const d = chartDaysRef.current;
    const dp = chartDeptRef.current ? `&department_id=${chartDeptRef.current}` : '';
    try {
      const [r1,r2,r3,r4,r5,r6,r7,r8,r9,r10] = await Promise.all([
        fetch(`/api/v1/dashboard/tickets-over-time?days=${d}${dp}`, { headers: h }),
        fetch(`/api/v1/dashboard/tickets-by-status?x=1${dp}`, { headers: h }),
        fetch(`/api/v1/dashboard/tickets-by-priority?x=1${dp}`, { headers: h }),
        fetch(`/api/v1/dashboard/tickets-by-department?x=1${dp}`, { headers: h }),
        fetch(`/api/v1/dashboard/avg-resolution-time?x=1${dp}`, { headers: h }),
        fetch(`/api/v1/dashboard/top-agents?x=1${dp}`, { headers: h }),
        fetch(`/api/v1/dashboard/conversations-over-time?days=${d}`, { headers: h }),
        fetch('/api/v1/dashboard/conversations-by-status', { headers: h }),
        fetch('/api/v1/dashboard/agent-workload', { headers: h }),
        fetch(`/api/v1/dashboard/bot-vs-human?days=${d}`, { headers: h }),
      ]);
      if (r1.ok) { const d = await r1.json(); if (d.success) setTicketsOverTime(d.data); }
      if (r2.ok) { const d = await r2.json(); if (d.success) setTicketsByStatus(d.data); }
      if (r3.ok) { const d = await r3.json(); if (d.success) setTicketsByPriority(d.data); }
      if (r4.ok) { const d = await r4.json(); if (d.success) setTicketsByDept(d.data); }
      if (r5.ok) { const d = await r5.json(); if (d.success) setAvgResolution(d.data); }
      if (r6.ok) { const d = await r6.json(); if (d.success) setTopAgents(d.data); }
      if (r7.ok) { const d = await r7.json(); if (d.success) setConvsOverTime(d.data); }
      if (r8.ok) { const d = await r8.json(); if (d.success) setConvsByStatus(d.data); }
      if (r9.ok) { const d = await r9.json(); if (d.success) setAgentWorkload(d.data); }
      if (r10.ok) { const d = await r10.json(); if (d.success) setBotVsHuman(d.data); }
    } catch (e) { console.error('chart data error:', e); }
  };

  useEffect(() => { chartDaysRef.current = chartDays; }, [chartDays]);
  useEffect(() => { chartDeptRef.current = chartDept; }, [chartDept]);
  useEffect(() => { fetchDepartments(); }, []);
  useEffect(() => { 
    fetchChartData(); 
    const chartInterval = setInterval(fetchChartData, 30000);
    return () => clearInterval(chartInterval);
  }, [chartDays, chartDept]);

  const formatTime = (timestamp) => {
    if (!timestamp) return 'Never';
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    return `${Math.floor(hours / 24)}d ago`;
  };

  const formatBookingTime = (time) => {
    if (!time) return '';
    const [h, m] = time.split(':');
    const hour = parseInt(h);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const h12 = hour % 12 || 12;
    return `${h12}:${m} ${ampm}`;
  };

  const priorityColors = { critical: '#ef4444', high: '#f97316', medium: '#eab308', low: '#22c55e' };
  const statusColors = { open: '#3b82f6', assigned: '#8b5cf6', in_progress: '#f59e0b', waiting_for_customer: '#6b7280', pending: '#f59e0b', confirmed: '#22c55e' };

  const CHART_COLORS = ['#3b82f6', '#8b5cf6', '#f59e0b', '#ef4444', '#22c55e', '#ec4899', '#06b6d4', '#f97316'];
  const STATUS_COLORS = { open: '#3b82f6', assigned: '#8b5cf6', in_progress: '#f59e0b', waiting_for_customer: '#6b7280', resolved: '#22c55e', closed: '#64748b', pending: '#f59e0b', active: '#22c55e', completed: '#3b82f6' };
  const PRIORITY_COLORS = { critical: '#ef4444', high: '#f97316', medium: '#eab308', low: '#22c55e' };

  const onlineAgents = agentsStatus.filter(a => a.is_online);
  const offlineAgents = agentsStatus.filter(a => !a.is_online);

  // Theme-aware colors
  const c = {
    textPrimary: isDark ? '#f9fafb' : '#111827',
    textSecondary: isDark ? '#e5e7eb' : '#374151',
    textTertiary: isDark ? '#d1d5db' : '#4b5563',
    textMuted: isDark ? '#9ca3af' : '#6b7280',
    textDisabled: isDark ? '#6b7280' : '#9ca3af',
    textFaint: isDark ? '#4b5563' : '#b0b5bc',
    cardBg: isDark ? 'rgba(255,255,255,0.04)' : '#ffffff',
    cardBorder: isDark ? 'rgba(255,255,255,0.08)' : '#e5e7eb',
    borderLight: isDark ? 'rgba(255,255,255,0.06)' : '#f3f4f6',
    borderMedium: isDark ? 'rgba(255,255,255,0.1)' : '#d1d5db',
    hoverBg: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.03)',
    rowBg: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.01)',
    totalRowBg: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.03)',
    shadow: isDark ? '0 4px 16px rgba(0,0,0,0.3)' : '0 4px 16px rgba(0,0,0,0.08)',
    onlineBg: isDark ? 'rgba(34,197,94,0.06)' : 'rgba(34,197,94,0.08)',
    offlineBorderLeft: isDark ? 'rgba(255,255,255,0.06)' : '#e5e7eb',
  };

  const styles = {
    dashboard: { padding: '20px', maxWidth: '1400px', margin: '0 auto' },
    header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' },
    headerTitle: { fontSize: '22px', fontWeight: '700', color: c.textPrimary, margin: 0 },
    headerSub: { fontSize: '13px', color: c.textMuted, margin: '4px 0 0' },
    row: { display: 'grid', gap: '16px', marginBottom: '16px' },
    row1: { gridTemplateColumns: '280px 1fr' },
    row2: { gridTemplateColumns: 'repeat(5, 1fr)' },
    row3: { gridTemplateColumns: '1fr' },
    row4: { gridTemplateColumns: '1fr 1fr 1fr' },
    row5: { gridTemplateColumns: '1fr 1fr' },
    row6: { gridTemplateColumns: '1fr 1fr 1fr' },
    chartHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' },
    filterBtn: (active) => ({ padding: '4px 12px', fontSize: '11px', borderRadius: '6px', border: `1px solid ${active ? '#3b82f6' : c.cardBorder}`, background: active ? 'rgba(59,130,246,0.15)' : 'transparent', color: active ? '#60a5fa' : c.textMuted, cursor: 'pointer', fontWeight: active ? '600' : '400' }),
    card: { background: c.cardBg, border: `1px solid ${c.cardBorder}`, borderRadius: '12px', padding: '16px', overflow: 'hidden', boxShadow: isDark ? 'none' : '0 1px 3px rgba(0,0,0,0.06)' },
    cardTitle: { fontSize: '14px', fontWeight: '600', color: c.textSecondary, marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' },
    statCard: { background: c.cardBg, border: `1px solid ${c.cardBorder}`, borderRadius: '12px', padding: '16px', cursor: 'pointer', transition: 'all 0.2s', textAlign: 'center', boxShadow: isDark ? 'none' : '0 1px 3px rgba(0,0,0,0.06)' },
    statNumber: { fontSize: '28px', fontWeight: '700', color: c.textPrimary, lineHeight: 1.2 },
    statLabel: { fontSize: '12px', color: c.textMuted, marginTop: '4px' },
    statusDot: (online) => ({ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: online ? '#22c55e' : '#ef4444', boxShadow: online ? '0 0 6px rgba(34,197,94,0.5)' : 'none', flexShrink: 0 }),
    systemItem: () => ({ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 0', borderBottom: `1px solid ${c.borderLight}` }),
    healthDot: (healthy) => ({ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: healthy ? '#22c55e' : '#ef4444', boxShadow: healthy ? '0 0 6px rgba(34,197,94,0.4)' : '0 0 6px rgba(239,68,68,0.4)' }),
    convBar: { display: 'flex', gap: '16px', alignItems: 'center', justifyContent: 'center', flexWrap: 'wrap' },
    convItem: (color) => ({ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '12px 24px', borderRadius: '10px', background: `${color}10`, border: `1px solid ${color}30`, minWidth: '120px' }),
    ticketRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 10px', borderRadius: '6px', backgroundColor: c.rowBg, marginBottom: '4px', cursor: 'pointer', transition: 'background 0.15s' },
    bookingRow: { display: 'flex', alignItems: 'center', gap: '12px', padding: '10px', borderRadius: '8px', backgroundColor: c.rowBg, marginBottom: '6px', borderLeft: '3px solid #8b5cf6' },
    emptyState: { textAlign: 'center', color: c.textDisabled, padding: '20px', fontSize: '13px' },
    badge: (color) => ({ fontSize: '10px', padding: '2px 6px', borderRadius: '4px', backgroundColor: `${color}20`, color: color, fontWeight: '600', textTransform: 'uppercase' }),
    scrollArea: { maxHeight: '320px', overflowY: 'auto', paddingRight: '4px' }
  };

  return (
    <Layout>
      <style>{`
        .dash-stat-card:hover { transform: translateY(-2px); box-shadow: ${c.shadow}; }
        .dash-ticket-row:hover { background: ${c.hoverBg} !important; }
        .dash-scroll::-webkit-scrollbar { width: 4px; }
        .dash-scroll::-webkit-scrollbar-track { background: transparent; }
        .dash-scroll::-webkit-scrollbar-thumb { background: ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.15)'}; border-radius: 4px; }
        @media (max-width: 1200px) {
          .dash-row-1 { grid-template-columns: 1fr !important; }
          .dash-row-2 { grid-template-columns: repeat(3, 1fr) !important; }
          .dash-row-4 { grid-template-columns: 1fr !important; }
          .dash-row-5 { grid-template-columns: 1fr !important; }
          .dash-row-6 { grid-template-columns: 1fr !important; }
          .dash-row-7 { grid-template-columns: 1fr !important; }
          .dash-row-8 { grid-template-columns: 1fr !important; }
        }
        @media (max-width: 768px) {
          .dash-row-2 { grid-template-columns: repeat(2, 1fr) !important; }
        }
      `}</style>

      <div style={styles.dashboard}>
        {/* Header */}
        <div style={styles.header}>
          <div>
            <h1 style={styles.headerTitle}>📊 Dashboard</h1>
            <p style={styles.headerSub}>Real-time overview · Auto-refreshes every 30s</p>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <Button variant="outline" onClick={fetchAll}>🔄 Refresh</Button>
            <Button variant="primary" onClick={() => navigate('/my-assignments')}>📋 My Assignments</Button>
          </div>
        </div>

        {/* ROW 1: System Status + Agent Status */}
        <div className="dash-row-1" style={{ ...styles.row, ...styles.row1 }}>
          {/* System Status */}
          <div style={styles.card}>
            <div style={styles.cardTitle}>🖥️ System Status</div>
            {[
              { name: 'Backend Server', healthy: systemHealth.backend },
              { name: 'WhatsApp API', healthy: systemHealth.whatsapp },
              { name: 'Database', healthy: systemHealth.database },
              { name: 'AI Service', healthy: systemHealth.ai }
            ].map((item, i) => (
              <div key={i} style={styles.systemItem()}>
                <span style={{ fontSize: '13px', color: c.textTertiary }}>{item.name}</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  
                  <span style={{ fontSize: '11px', color: item.healthy ? '#22c55e' : '#ef4444' }}>
                    {item.healthy ? 'Online' : 'Down'}
                  </span>
                </div>
              </div>
            ))}
            <div style={{ marginTop: '8px', fontSize: '11px', color: c.textDisabled, textAlign: 'center' }}>Last check: Just now</div>
          </div>

          {/* Agent Status */}
          <div style={styles.card}>
            <div style={{ ...styles.cardTitle, justifyContent: 'space-between' }}>
              <span>👥 Agent Status</span>
              <div style={{ display: 'flex', gap: '8px' }}>
                <span style={styles.badge('#22c55e')}>{onlineAgents.length} Online</span>
                <span style={styles.badge('#6b7280')}>{offlineAgents.length} Offline</span>
              </div>
            </div>
            <div className="dash-scroll" style={styles.scrollArea}>
              {/* Table header */}
              <div style={{
                display: 'grid', gridTemplateColumns: '1fr 50px 65px 65px 55px 60px',
                padding: '4px 10px', fontSize: '10px', color: c.textDisabled, fontWeight: '600',
                textTransform: 'uppercase', borderBottom: `1px solid ${c.borderLight}`, marginBottom: '4px'
              }}>
                <span>Agent</span>
                <span style={{ textAlign: 'center' }}>💬</span>
                <span style={{ textAlign: 'center', color: '#8b5cf6' }}>Assigned</span>
                <span style={{ textAlign: 'center', color: '#f59e0b' }}>Progress</span>
                <span style={{ textAlign: 'center', color: c.textDisabled }}>Other</span>
                <span style={{ textAlign: 'right' }}>Status</span>
              </div>
              {agentsStatus.map(agent => (
                <div key={agent.id} style={{
                  display: 'grid', gridTemplateColumns: '1fr 50px 65px 65px 55px 60px',
                  alignItems: 'center', padding: '6px 10px', borderRadius: '6px',
                  backgroundColor: agent.is_online ? c.onlineBg : 'transparent',
                  borderLeft: `3px solid ${agent.is_online ? '#22c55e' : c.offlineBorderLeft}`,
                  marginBottom: '3px'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', minWidth: 0 }}>
                   
                    <span style={{ fontSize: '12px', fontWeight: agent.is_online ? '600' : '400', color: agent.is_online ? c.textPrimary : c.textMuted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {agent.full_name}
                    </span>
                  </div>
                  <span style={{ textAlign: 'center', fontSize: '12px', fontWeight: '600', color: agent.conversations_today > 0 ? '#60a5fa' : c.textFaint }}>
                    {agent.conversations_today || 0}
                  </span>
                  <span style={{ textAlign: 'center', fontSize: '12px', fontWeight: '600', color: agent.tickets_assigned > 0 ? '#a78bfa' : c.textFaint }}>
                    {agent.tickets_assigned || 0}
                  </span>
                  <span style={{ textAlign: 'center', fontSize: '12px', fontWeight: '600', color: agent.tickets_in_progress > 0 ? '#fbbf24' : c.textFaint }}>
                    {agent.tickets_in_progress || 0}
                  </span>
                  <span style={{ textAlign: 'center', fontSize: '12px', fontWeight: '600', color: agent.tickets_other > 0 ? '#9ca3af' : c.textFaint }}>
                    {agent.tickets_other || 0}
                  </span>
                  <span style={{ textAlign: 'right', fontSize: '10px', color: c.textDisabled }}>
                    {agent.is_online ? '🟢' : agent.last_seen ? formatTime(agent.last_seen) : 'Never'}
                  </span>
                </div>
              ))}
              {agentsStatus.length === 0 && <div style={styles.emptyState}>No agents configured</div>}
            </div>
          </div>
        </div>

        {/* ROW 2: Stat Cards */}
        <div className="dash-row-2" style={{ ...styles.row, ...styles.row2 }}>
          <div className="dash-stat-card" style={styles.statCard} onClick={() => navigate('/my-assignments')}>
            <div style={{ fontSize: '24px', marginBottom: '4px' }}>📋</div>
            <div style={{ ...styles.statNumber, color: '#60a5fa' }}>{assignments.active_assignments}</div>
            <div style={styles.statLabel}>Active Assignments</div>
          </div>
          <div className="dash-stat-card" style={styles.statCard} onClick={() => navigate('/queue')}>
            <div style={{ fontSize: '24px', marginBottom: '4px' }}>📥</div>
            <div style={{ ...styles.statNumber, color: '#f59e0b' }}>{assignments.in_queue}</div>
            <div style={styles.statLabel}>In Queue</div>
          </div>
          <div className="dash-stat-card" style={styles.statCard} onClick={() => navigate('/helpdesk')}>
            <div style={{ fontSize: '24px', marginBottom: '4px' }}>🎫</div>
            <div style={{ ...styles.statNumber, color: '#8b5cf6' }}>{helpdeskSummary.open_tickets}</div>
            <div style={styles.statLabel}>Open Tickets</div>
          </div>
          <div className="dash-stat-card" style={{ ...styles.statCard, border: helpdeskSummary.sla_violations > 0 ? '1px solid rgba(239,68,68,0.3)' : undefined }}>
            <div style={{ fontSize: '24px', marginBottom: '4px' }}>⚠️</div>
            <div style={{ ...styles.statNumber, color: helpdeskSummary.sla_violations > 0 ? '#ef4444' : '#22c55e' }}>{helpdeskSummary.sla_violations}</div>
            <div style={styles.statLabel}>SLA Violations</div>
          </div>
          <div className="dash-stat-card" style={styles.statCard} onClick={() => navigate('/my-bookings')}>
            <div style={{ fontSize: '24px', marginBottom: '4px' }}>📅</div>
            <div style={{ ...styles.statNumber, color: '#a78bfa' }}>{todayBookings.count}</div>
            <div style={styles.statLabel}>Today's Bookings</div>
          </div>
        </div>

        {/* ROW: Department Pivot */}
        <div style={{ ...styles.row, gridTemplateColumns: '1fr' }}>
          <div style={styles.card}>
            <div style={styles.cardTitle}>🏢 Tickets by Department</div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                <thead>
                  <tr style={{ borderBottom: `1px solid ${c.borderMedium}` }}>
                    <th style={{ padding: '8px 10px', textAlign: 'left', color: c.textMuted, fontWeight: '600' }}>Department</th>
                    <th style={{ padding: '8px 6px', textAlign: 'center', color: '#3b82f6', fontWeight: '600' }}>Open</th>
                    <th style={{ padding: '8px 6px', textAlign: 'center', color: '#8b5cf6', fontWeight: '600' }}>Assigned</th>
                    <th style={{ padding: '8px 6px', textAlign: 'center', color: '#f59e0b', fontWeight: '600' }}>In Progress</th>
                    <th style={{ padding: '8px 6px', textAlign: 'center', color: c.textDisabled, fontWeight: '600' }}>Waiting</th>
                    <th style={{ padding: '8px 6px', textAlign: 'center', color: '#22c55e', fontWeight: '600' }}>Resolved</th>
                    <th style={{ padding: '8px 6px', textAlign: 'center', color: c.textFaint, fontWeight: '600' }}>Closed</th>
                    <th style={{ padding: '8px 6px', textAlign: 'center', color: c.textSecondary, fontWeight: '700' }}>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {deptPivot.map((dept, i) => (
                    <tr key={i} style={{ borderBottom: `1px solid ${c.borderLight}` }}>
                      <td style={{ padding: '8px 10px', color: c.textSecondary, fontWeight: '500' }}>{dept.department}</td>
                      <td style={{ padding: '8px 6px', textAlign: 'center', color: dept.open_count > 0 ? '#60a5fa' : c.textFaint, fontWeight: dept.open_count > 0 ? '600' : '400' }}>{dept.open_count}</td>
                      <td style={{ padding: '8px 6px', textAlign: 'center', color: dept.assigned_count > 0 ? '#a78bfa' : c.textFaint, fontWeight: dept.assigned_count > 0 ? '600' : '400' }}>{dept.assigned_count}</td>
                      <td style={{ padding: '8px 6px', textAlign: 'center', color: dept.in_progress_count > 0 ? '#fbbf24' : c.textFaint, fontWeight: dept.in_progress_count > 0 ? '600' : '400' }}>{dept.in_progress_count}</td>
                      <td style={{ padding: '8px 6px', textAlign: 'center', color: dept.waiting_count > 0 ? '#9ca3af' : c.textFaint, fontWeight: dept.waiting_count > 0 ? '600' : '400' }}>{dept.waiting_count}</td>
                      <td style={{ padding: '8px 6px', textAlign: 'center', color: dept.resolved_count > 0 ? '#4ade80' : c.textFaint, fontWeight: dept.resolved_count > 0 ? '600' : '400' }}>{dept.resolved_count}</td>
                      <td style={{ padding: '8px 6px', textAlign: 'center', color: c.textFaint }}>{dept.closed_count}</td>
                      <td style={{ padding: '8px 6px', textAlign: 'center', color: c.textPrimary, fontWeight: '700' }}>{dept.total}</td>
                    </tr>
                  ))}
                  {deptPivot.length > 0 && (
                    <tr style={{ borderTop: `2px solid ${c.borderMedium}`, backgroundColor: c.totalRowBg }}>
                      <td style={{ padding: '8px 10px', color: c.textPrimary, fontWeight: '700' }}>Total</td>
                      <td style={{ padding: '8px 6px', textAlign: 'center', color: '#60a5fa', fontWeight: '700' }}>{deptPivot.reduce((s, d) => s + (parseInt(d.open_count) || 0), 0)}</td>
                      <td style={{ padding: '8px 6px', textAlign: 'center', color: '#a78bfa', fontWeight: '700' }}>{deptPivot.reduce((s, d) => s + (parseInt(d.assigned_count) || 0), 0)}</td>
                      <td style={{ padding: '8px 6px', textAlign: 'center', color: '#fbbf24', fontWeight: '700' }}>{deptPivot.reduce((s, d) => s + (parseInt(d.in_progress_count) || 0), 0)}</td>
                      <td style={{ padding: '8px 6px', textAlign: 'center', color: '#9ca3af', fontWeight: '700' }}>{deptPivot.reduce((s, d) => s + (parseInt(d.waiting_count) || 0), 0)}</td>
                      <td style={{ padding: '8px 6px', textAlign: 'center', color: '#4ade80', fontWeight: '700' }}>{deptPivot.reduce((s, d) => s + (parseInt(d.resolved_count) || 0), 0)}</td>
                      <td style={{ padding: '8px 6px', textAlign: 'center', color: c.textFaint, fontWeight: '700' }}>{deptPivot.reduce((s, d) => s + (parseInt(d.closed_count) || 0), 0)}</td>
                      <td style={{ padding: '8px 6px', textAlign: 'center', color: c.textPrimary, fontWeight: '700' }}>{deptPivot.reduce((s, d) => s + (parseInt(d.total) || 0), 0)}</td>
                      </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* ROW: Conversations Today */}
        <div style={{ ...styles.row, ...styles.row3 }}>
          <div style={styles.card}>
            <div style={{ ...styles.cardTitle, justifyContent: 'space-between' }}>
              <span>💬 Conversations Today (since 7:00 AM)</span>
              <span style={{ fontSize: '11px', color: c.textDisabled, fontWeight: '400' }}>{convStats.active_now} active now</span>
            </div>
            <div style={styles.convBar}>
              <div style={styles.convItem('#60a5fa')}>
                <span style={{ fontSize: '26px', fontWeight: '700', color: '#60a5fa' }}>{convStats.total}</span>
                <span style={{ fontSize: '12px', color: c.textMuted, marginTop: '2px' }}>Total</span>
              </div>
              <div style={styles.convItem('#22c55e')}>
                <span style={{ fontSize: '26px', fontWeight: '700', color: '#22c55e' }}>{convStats.pure_ai}</span>
                <span style={{ fontSize: '12px', color: c.textMuted, marginTop: '2px' }}>🤖 Pure AI</span>
              </div>
              <div style={styles.convItem('#f59e0b')}>
                <span style={{ fontSize: '26px', fontWeight: '700', color: '#f59e0b' }}>{convStats.agent}</span>
                <span style={{ fontSize: '12px', color: c.textMuted, marginTop: '2px' }}>👤 Agent</span>
              </div>
              {convStats.total > 0 && (
                <div style={styles.convItem('#8b5cf6')}>
                  <span style={{ fontSize: '26px', fontWeight: '700', color: '#8b5cf6' }}>{Math.round((convStats.pure_ai / convStats.total) * 100)}%</span>
                  <span style={{ fontSize: '12px', color: c.textMuted, marginTop: '2px' }}>🤖 AI Rate</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ROW 4: Three Columns */}
        <div className="dash-row-4" style={{ ...styles.row, ...styles.row4 }}>
          {/* Recent WhatsApp */}
          <div style={styles.card}>
            <div style={{ ...styles.cardTitle, justifyContent: 'space-between' }}>
              <span>📱 Recent WhatsApp</span>
              <Badge variant="success" dot size="sm">Live</Badge>
            </div>
            <div className="dash-scroll" style={styles.scrollArea}>
              {recentActivity.length === 0 ? (
                <div style={styles.emptyState}>No recent activity</div>
              ) : (
                recentActivity.map((act, i) => (
                  <div key={i} className="dash-ticket-row" style={styles.ticketRow}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '13px', fontWeight: '500', color: c.textSecondary, marginBottom: '2px' }}>{act.user_name || act.phone_number}</div>
                      <div style={{ fontSize: '12px', color: c.textDisabled, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{act.last_message || 'No message'}</div>
                    </div>
                    <span style={{ fontSize: '11px', color: c.textDisabled, flexShrink: 0, marginLeft: '8px' }}>{formatTime(act.timestamp)}</span>
                  </div>
                ))
              )}
            </div>
            <div style={{ marginTop: '10px', textAlign: 'center' }}>
              <Button variant="ghost" onClick={() => navigate('/all-conversations')} style={{ fontSize: '12px' }}>View All Conversations →</Button>
            </div>
          </div>

          {/* Helpdesk Tickets */}
          <div style={styles.card}>
            <div style={{ ...styles.cardTitle, justifyContent: 'space-between' }}>
              <span>🎫 Open Tickets</span>
              <span style={styles.badge('#8b5cf6')}>{helpdeskSummary.my_tickets} mine</span>
            </div>
            <div className="dash-scroll" style={styles.scrollArea}>
              {helpdeskSummary.recent_tickets.length === 0 ? (
                <div style={styles.emptyState}>No open tickets</div>
              ) : (
                helpdeskSummary.recent_tickets.map((ticket, i) => (
                  <div key={i} className="dash-ticket-row" style={styles.ticketRow} onClick={() => navigate(`/helpdesk/tickets/${ticket.id}`)}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '2px' }}>
                        <span style={{ fontSize: '11px', color: c.textDisabled }}>{ticket.ticket_number}</span>
                        <span style={styles.badge(priorityColors[ticket.priority] || '#6b7280')}>{ticket.priority}</span>
                      </div>
                      <div style={{ fontSize: '12px', color: c.textTertiary, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{ticket.subject}</div>
                      <div style={{ fontSize: '11px', color: c.textFaint, marginTop: '2px' }}>{ticket.department_name || 'No dept'} · {ticket.assigned_agent || 'Unassigned'}</div>
                    </div>
                    <span style={{ ...styles.badge(statusColors[ticket.status] || '#6b7280'), flexShrink: 0, marginLeft: '6px', fontSize: '9px' }}>{ticket.status.replace(/_/g, ' ')}</span>
                  </div>
                ))
              )}
            </div>
            <div style={{ marginTop: '10px', textAlign: 'center' }}>
              <Button variant="ghost" onClick={() => navigate('/helpdesk')} style={{ fontSize: '12px' }}>View All Tickets →</Button>
            </div>
          </div>

          {/* Today's Bookings */}
          <div style={styles.card}>
            <div style={{ ...styles.cardTitle, justifyContent: 'space-between' }}>
              <span>📅 Today's Schedule</span>
              <span style={styles.badge('#a78bfa')}>{todayBookings.count} meetings</span>
            </div>
            <div className="dash-scroll" style={styles.scrollArea}>
              {todayBookings.bookings.length === 0 ? (
                <div style={styles.emptyState}>No bookings today</div>
              ) : (
                todayBookings.bookings.map((booking, i) => (
                  <div key={i} style={styles.bookingRow}>
                    <div style={{ background: 'rgba(139,92,246,0.15)', borderRadius: '6px', padding: '6px 10px', textAlign: 'center', minWidth: '65px' }}>
                      <div style={{ fontSize: '12px', fontWeight: '700', color: '#a78bfa' }}>{formatBookingTime(booking.start_time)}</div>
                      <div style={{ fontSize: '10px', color: c.textDisabled }}>{formatBookingTime(booking.end_time)}</div>
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '13px', fontWeight: '500', color: c.textSecondary }}>{booking.customer_name}</div>
                      <div style={{ fontSize: '11px', color: c.textDisabled }}>{booking.customer_phone}{booking.agent_name ? ` · ${booking.agent_name}` : ''}</div>
                    </div>
                    <span style={styles.badge(statusColors[booking.status] || '#6b7280')}>{booking.status}</span>
                  </div>
                ))
              )}
            </div>
            <div style={{ marginTop: '10px', textAlign: 'center' }}>
              <Button variant="ghost" onClick={() => navigate('/my-bookings')} style={{ fontSize: '12px' }}>View All Bookings →</Button>
            </div>
          </div>
        </div>
        {/* ===== CHARTS SECTION ===== */}
        <div style={{ marginTop: '24px', marginBottom: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 style={{ fontSize: '18px', fontWeight: '700', color: c.textPrimary, margin: 0 }}>📈 Analytics</h2>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <select 
              value={chartDept} 
              onChange={(e) => setChartDept(e.target.value)}
              style={{ padding: '4px 10px', fontSize: '12px', borderRadius: '6px', border: `1px solid ${c.cardBorder}`, background: c.cardBg, color: c.textSecondary, cursor: 'pointer', outline: 'none' }}
            >
              <option value="">All Departments</option>
              {departments.map(d => (
                <option key={d.id} value={d.id}>{d.name}</option>
              ))}
            </select>
            <div style={{ display: 'flex', gap: '6px' }}>
              {[7, 14, 30, 90].map(d => (
                <button key={d} style={styles.filterBtn(chartDays === d)} onClick={() => setChartDays(d)}>
                  {d}d
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* ROW 5: Tickets Over Time + Conversations Over Time */}
        <div className="dash-row-5" style={{ ...styles.row, ...styles.row5 }}>
          <div style={styles.card}>
            <div style={styles.cardTitle}>🎫 Tickets Over Time</div>
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={ticketsOverTime}>
                <CartesianGrid strokeDasharray="3 3" stroke={c.borderLight} />
                <XAxis dataKey="date" tick={{ fontSize: 11, fill: c.textMuted }} />
                <YAxis tick={{ fontSize: 11, fill: c.textMuted }} allowDecimals={false} />
                <Tooltip contentStyle={{ background: isDark ? '#1e293b' : '#fff', border: `1px solid ${c.cardBorder}`, borderRadius: '8px', fontSize: '12px' }} />
                <Legend wrapperStyle={{ fontSize: '12px' }} />
                <Line type="monotone" dataKey="created" stroke="#3b82f6" strokeWidth={2} dot={{ r: 3 }} name="Created" />
                <Line type="monotone" dataKey="resolved" stroke="#22c55e" strokeWidth={2} dot={{ r: 3 }} name="Resolved" />
              </LineChart>
            </ResponsiveContainer>
          </div>

          <div style={styles.card}>
            <div style={styles.cardTitle}>💬 Conversations Over Time</div>
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={convsOverTime}>
                <CartesianGrid strokeDasharray="3 3" stroke={c.borderLight} />
                <XAxis dataKey="date" tick={{ fontSize: 11, fill: c.textMuted }} />
                <YAxis tick={{ fontSize: 11, fill: c.textMuted }} allowDecimals={false} />
                <Tooltip contentStyle={{ background: isDark ? '#1e293b' : '#fff', border: `1px solid ${c.cardBorder}`, borderRadius: '8px', fontSize: '12px' }} />
                <Legend wrapperStyle={{ fontSize: '12px' }} />
                <Line type="monotone" dataKey="total" stroke="#8b5cf6" strokeWidth={2} dot={{ r: 3 }} name="Total" />
                <Line type="monotone" dataKey="bot_only" stroke="#06b6d4" strokeWidth={2} dot={{ r: 3 }} name="Bot Only" />
                <Line type="monotone" dataKey="agent_handled" stroke="#f59e0b" strokeWidth={2} dot={{ r: 3 }} name="Agent" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* ROW 6: Status Pie + Priority Bar + Department Bar */}
        <div className="dash-row-6" style={{ ...styles.row, ...styles.row6 }}>
          <div style={styles.card}>
            <div style={styles.cardTitle}>📊 Tickets by Status</div>
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie data={ticketsByStatus} dataKey="count" nameKey="status" cx="50%" cy="50%" outerRadius={80} label={({ status, count }) => `${status.replace(/_/g, ' ')} (${count})`} labelLine={true} style={{ fontSize: '11px' }}>
                  {ticketsByStatus.map((entry, i) => (
                    <Cell key={i} fill={STATUS_COLORS[entry.status] || CHART_COLORS[i % CHART_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip contentStyle={{ background: isDark ? '#1e293b' : '#fff', border: `1px solid ${c.cardBorder}`, borderRadius: '8px', fontSize: '12px' }} />
              </PieChart>
            </ResponsiveContainer>
          </div>

          <div style={styles.card}>
            <div style={styles.cardTitle}>⚡ Tickets by Priority</div>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={ticketsByPriority}>
                <CartesianGrid strokeDasharray="3 3" stroke={c.borderLight} />
                <XAxis dataKey="priority" tick={{ fontSize: 11, fill: c.textMuted }} />
                <YAxis tick={{ fontSize: 11, fill: c.textMuted }} allowDecimals={false} />
                <Tooltip contentStyle={{ background: isDark ? '#1e293b' : '#fff', border: `1px solid ${c.cardBorder}`, borderRadius: '8px', fontSize: '12px' }} />
                <Bar dataKey="count" radius={[6, 6, 0, 0]}>
                  {ticketsByPriority.map((entry, i) => (
                    <Cell key={i} fill={PRIORITY_COLORS[entry.priority] || CHART_COLORS[i]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div style={styles.card}>
            <div style={styles.cardTitle}>🏢 Tickets by Department</div>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={ticketsByDept} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke={c.borderLight} />
                <XAxis type="number" tick={{ fontSize: 11, fill: c.textMuted }} allowDecimals={false} />
                <YAxis type="category" dataKey="department" tick={{ fontSize: 11, fill: c.textMuted }} width={100} />
                <Tooltip contentStyle={{ background: isDark ? '#1e293b' : '#fff', border: `1px solid ${c.cardBorder}`, borderRadius: '8px', fontSize: '12px' }} />
                <Bar dataKey="count" radius={[0, 6, 6, 0]}>
                  {ticketsByDept.map((entry, i) => (
                    <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* ROW 7: Avg Resolution + Top Agents + Bot vs Human */}
        <div className="dash-row-7" style={{ ...styles.row, ...styles.row6 }}>
          <div style={styles.card}>
            <div style={styles.cardTitle}>⏱️ Avg Resolution Time (hrs)</div>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={avgResolution} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke={c.borderLight} />
                <XAxis type="number" tick={{ fontSize: 11, fill: c.textMuted }} />
                <YAxis type="category" dataKey="department" tick={{ fontSize: 11, fill: c.textMuted }} width={100} />
                <Tooltip contentStyle={{ background: isDark ? '#1e293b' : '#fff', border: `1px solid ${c.cardBorder}`, borderRadius: '8px', fontSize: '12px' }} />
                <Bar dataKey="avg_hours" fill="#f59e0b" radius={[0, 6, 6, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div style={styles.card}>
            <div style={styles.cardTitle}>🏆 Top Agents (Resolved)</div>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={topAgents} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke={c.borderLight} />
                <XAxis type="number" tick={{ fontSize: 11, fill: c.textMuted }} allowDecimals={false} />
                <YAxis type="category" dataKey="agent" tick={{ fontSize: 11, fill: c.textMuted }} width={120} />
                <Tooltip contentStyle={{ background: isDark ? '#1e293b' : '#fff', border: `1px solid ${c.cardBorder}`, borderRadius: '8px', fontSize: '12px' }} />
                <Bar dataKey="resolved" fill="#22c55e" radius={[0, 6, 6, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div style={styles.card}>
            <div style={styles.cardTitle}>🤖 Bot vs Human</div>
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie data={[
                  { name: 'Bot Only', value: botVsHuman.bot_only || 0 },
                  { name: 'Agent', value: botVsHuman.agent_handled || 0 }
                ]} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label={({ name, value }) => `${name}: ${value}`} style={{ fontSize: '12px' }}>
                  <Cell fill="#06b6d4" />
                  <Cell fill="#f59e0b" />
                </Pie>
                <Tooltip contentStyle={{ background: isDark ? '#1e293b' : '#fff', border: `1px solid ${c.cardBorder}`, borderRadius: '8px', fontSize: '12px' }} />
                <Legend wrapperStyle={{ fontSize: '12px' }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* ROW 8: Conversations by Status + Agent Workload */}
        <div className="dash-row-8" style={{ ...styles.row, ...styles.row5 }}>
          <div style={styles.card}>
            <div style={styles.cardTitle}>💬 Conversations by Status</div>
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie data={convsByStatus} dataKey="count" nameKey="status" cx="50%" cy="50%" outerRadius={80} label={({ status, count }) => `${status} (${count})`} style={{ fontSize: '11px' }}>
                  {convsByStatus.map((entry, i) => (
                    <Cell key={i} fill={STATUS_COLORS[entry.status] || CHART_COLORS[i % CHART_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip contentStyle={{ background: isDark ? '#1e293b' : '#fff', border: `1px solid ${c.cardBorder}`, borderRadius: '8px', fontSize: '12px' }} />
              </PieChart>
            </ResponsiveContainer>
          </div>

          <div style={styles.card}>
            <div style={styles.cardTitle}>👥 Agent Workload</div>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={agentWorkload}>
                <CartesianGrid strokeDasharray="3 3" stroke={c.borderLight} />
                <XAxis dataKey="agent" tick={{ fontSize: 10, fill: c.textMuted }} angle={-20} textAnchor="end" height={50} />
                <YAxis tick={{ fontSize: 11, fill: c.textMuted }} allowDecimals={false} />
                <Tooltip contentStyle={{ background: isDark ? '#1e293b' : '#fff', border: `1px solid ${c.cardBorder}`, borderRadius: '8px', fontSize: '12px' }} />
                <Legend wrapperStyle={{ fontSize: '12px' }} />
                <Bar dataKey="active" stackId="a" fill="#f59e0b" name="Active" radius={[0, 0, 0, 0]} />
                <Bar dataKey="completed" stackId="a" fill="#22c55e" name="Completed" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

      </div>
    </Layout>
  );
};

export default Dashboard;